import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import BookingForm from '../components/BookingForm';
import FeaturedRoutePromotion from '../components/FeaturedRoutePromotion';
import SeoHead from '../components/SeoHead';
import {
  VEHICLES,
  DRIVER_BATA,
  MIN_ONEWAY_KM,
  getVehiclePrice,
  getVehicleDisplayName,
} from '../data/bookingConfig';
import '../styles/RouteLanding.css';

const ROUTE_PATH = '/chennai-to-madurai-taxi';
const DEFAULT_PICKUP = 'Chennai';
const DEFAULT_DROP = 'Madurai';
const BOOKING_ID = 'route-booking';

const getRouteMetrics = async (origin, destination) => {
  try {
    if (!window.google) return null;
    const service = new window.google.maps.DistanceMatrixService();
    const response = await service.getDistanceMatrix({
      origins: [origin],
      destinations: [destination],
      travelMode: window.google.maps.TravelMode.DRIVING,
      unitSystem: window.google.maps.UnitSystem.METRIC,
    });
    const element = response.rows?.[0]?.elements?.[0];
    if (element?.status === 'OK' && element.distance) {
      return {
        distanceKm: Math.round((element.distance.value / 1000) * 10) / 10,
        durationText: element.duration?.text || null,
      };
    }
  } catch (error) {
    console.error('Route metrics error:', error);
  }
  return null;
};

const scrollToBooking = () => {
  const target = document.getElementById(BOOKING_ID);
  if (target) {
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const faqs = [
  {
    question: 'How can I book a Chennai to Madurai taxi?',
    answer:
      'Use the booking form on this page. Enter your pickup and drop locations, mobile number, then choose a vehicle, date and time. After you review the details, confirm the booking. Easy Drop Taxi will receive the request through the same booking process used on the homepage.',
  },
  {
    question: 'Can I book a one-way taxi from Chennai to Madurai?',
    answer:
      'Yes. One Way is the default trip type on this page. You can also switch to Round Trip in the booking card if you need a return journey.',
  },
  {
    question: 'Which vehicles are available?',
    answer:
      'The booking system currently offers Sedan, SUV and Crysta options. Each option shows its per-kilometre rate after you search, along with the driver bata included in the estimate.',
  },
  {
    question: 'Can I choose my pickup location in Chennai?',
    answer:
      'Yes. Pickup is pre-filled as Chennai, but you can edit it to a more specific address using the location field and Google Places suggestions.',
  },
  {
    question: 'Can I select a specific pickup time?',
    answer:
      'Yes. After you search for cabs, the next step asks for pickup date and time, along with your name and vehicle choice.',
  },
  {
    question: 'How is the fare calculated?',
    answer:
      `The fare uses Easy Drop Taxi’s existing formula: billed distance × vehicle rate + ₹${DRIVER_BATA} driver bata. One-way trips have a minimum billed distance of ${MIN_ONEWAY_KM} km. Toll, state permit, hill station, parking and waiting-time charges are extra, as shown during booking review.`,
  },
  {
    question: 'Can I book a Chennai Airport to Madurai taxi?',
    answer:
      'Yes. Change the pickup field to Chennai Airport or another airport address, keep Madurai as the drop (or edit it), then continue with the same booking steps.',
  },
];

const ChennaiToMadurai = () => {
  const [preferredVehicle, setPreferredVehicle] = useState('');
  const [routePrefill, setRoutePrefill] = useState(null);
  const [routeMetrics, setRouteMetrics] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadMetrics = async () => {
      const metrics = await getRouteMetrics(DEFAULT_PICKUP, DEFAULT_DROP);
      if (!cancelled && metrics) setRouteMetrics(metrics);
    };

    if (window.google) {
      loadMetrics();
    } else {
      window.addEventListener('google-maps-ready', loadMetrics, { once: true });
    }

    return () => {
      cancelled = true;
      window.removeEventListener('google-maps-ready', loadMetrics);
    };
  }, []);

  const jsonLd = useMemo(() => ([
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://easydroptaxi.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Chennai to Madurai Taxi',
          item: `https://easydroptaxi.com${ROUTE_PATH}`,
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Chennai to Madurai Taxi',
      serviceType: 'One-way taxi booking',
      provider: {
        '@type': 'TaxiService',
        name: 'Easy Drop Taxi',
        url: 'https://easydroptaxi.com/',
        telephone: '+919629954544',
      },
      areaServed: ['Chennai', 'Madurai', 'Tamil Nadu'],
      url: `https://easydroptaxi.com${ROUTE_PATH}`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    },
  ]), []);

  const handleSelectVehicle = (vehicleType) => {
    setPreferredVehicle(vehicleType);
    scrollToBooking();
  };

  return (
    <main className="route-landing">
      <SeoHead
        title="Chennai to Madurai Taxi | One Way Cab | Easy Drop Taxi"
        description="Book a Chennai to Madurai taxi with EasyDropTaxi. Choose your vehicle, enter your travel details and get your fare estimate for a convenient one-way cab journey."
        path={ROUTE_PATH}
        jsonLd={jsonLd}
      />

      <section className="rl-hero">
        <div className="rl-hero-inner">
          <div>
            <nav className="rl-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span aria-hidden="true">/</span>
              <span>Chennai to Madurai Taxi</span>
            </nav>
            <h1>Chennai to Madurai Taxi</h1>
            <p className="rl-hero-lead">
              Book a reliable one-way taxi from Chennai to Madurai with EasyDropTaxi.
            </p>
            <div className="rl-hero-note">
              <span><i className="fa fa-road" aria-hidden="true"></i>One Way Taxi</span>
              <span><i className="fa fa-phone" aria-hidden="true"></i><a href="tel:+919629954544">+91 96299 54544</a></span>
            </div>
          </div>

          <div id={BOOKING_ID} className="rl-booking-card">
            <BookingForm
              initialPickup={routePrefill?.pickup || DEFAULT_PICKUP}
              initialDrop={routePrefill?.drop || DEFAULT_DROP}
              initialTripType="One Way"
              initialVehicle={preferredVehicle}
              searchButtonLabel="Get Fare Estimate"
            />
          </div>
        </div>
      </section>

      <section className="rl-strip" aria-label="Route information">
        <div className="container">
          <div className="rl-strip-grid">
            <div className="rl-strip-item">
              <span className="rl-strip-label">Route</span>
              <span className="rl-strip-value">Chennai → Madurai</span>
            </div>
            <div className="rl-strip-item">
              <span className="rl-strip-label">Trip Type</span>
              <span className="rl-strip-value">One Way Taxi</span>
            </div>
            <div className="rl-strip-item">
              <span className="rl-strip-label">Approx. Distance</span>
              <span className="rl-strip-value">
                {routeMetrics?.distanceKm
                  ? `${routeMetrics.distanceKm} km`
                  : 'Shown after fare estimate'}
              </span>
            </div>
            <div className="rl-strip-item">
              <span className="rl-strip-label">Approx. Travel Time</span>
              <span className="rl-strip-value">
                {routeMetrics?.durationText || 'Calculated for your pickup'}
              </span>
            </div>
          </div>
        </div>
      </section>

      <FeaturedRoutePromotion
        origin={DEFAULT_PICKUP}
        destination={DEFAULT_DROP}
        onGetFareEstimate={({ origin, destination, vehicleType }) => {
          setRoutePrefill({ pickup: origin, drop: destination });
          setPreferredVehicle(vehicleType);
          const target = document.getElementById(BOOKING_ID);
          if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
      />

      <section className="rl-section rl-section-muted">
        <div className="container">
          <h2>Choose Your Taxi</h2>
          <p className="rl-section-intro">
            These are the vehicles available in the Easy Drop Taxi booking system. Rates follow the same per-kilometre tariff used across the website.
          </p>
          <div className="rl-vehicle-grid">
            {VEHICLES.map((vehicle) => (
              <article key={vehicle.type} className="rl-vehicle-card">
                <div className="rl-vehicle-media">
                  <img
                    src={vehicle.fleetImage || vehicle.image}
                    alt={`${getVehicleDisplayName(vehicle)} taxi for Chennai to Madurai travel`}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </div>
                <div className="rl-vehicle-body">
                  <h3>{getVehicleDisplayName(vehicle)}</h3>
                  <p>{vehicle.description}</p>
                  <div className="rl-vehicle-meta">
                    <span><i className="fa fa-users" aria-hidden="true"></i>{vehicle.seats}</span>
                    <span><i className="fa fa-tag" aria-hidden="true"></i>₹{vehicle.rate}/km</span>
                  </div>
                  {routeMetrics?.distanceKm ? (
                    <p>
                      Estimated one-way fare: ₹
                      {getVehiclePrice(routeMetrics.distanceKm, vehicle.rate, 'One Way').toLocaleString('en-IN')}
                    </p>
                  ) : (
                    <p>Use Get Fare Estimate in the booking card for your exact total.</p>
                  )}
                  <button
                    type="button"
                    className={`rl-select-btn${preferredVehicle === vehicle.type ? ' is-selected' : ''}`}
                    onClick={() => handleSelectVehicle(vehicle.type)}
                  >
                    {preferredVehicle === vehicle.type ? 'Selected' : 'Select'}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="rl-section">
        <div className="container">
          <h2>Chennai to Madurai Taxi</h2>
          <div className="rl-prose">
            <p>
              Planning an intercity trip between Chennai and Madurai is simpler when pickup and drop are handled door to door. Easy Drop Taxi lets you book a one-way cab online, choose a vehicle that fits your group, and confirm travel details in a few steps.
            </p>
            <p>
              Enter a Chennai pickup that works for you — home, hotel, office or another point on the map — and a Madurai drop for your destination. The same form is used for family visits, business travel and personal trips, so you do not need a separate booking process.
            </p>
            <p>
              Advance booking is available through the date and time fields after you search. A professional driver is assigned through the regular Easy Drop Taxi booking flow, and the team follows up on the request after you confirm.
            </p>
          </div>
        </div>
      </section>

      <section className="rl-section rl-section-muted">
        <div className="container">
          <h2>Why Choose EasyDropTaxi</h2>
          <div className="rl-benefit-grid">
            <article className="rl-benefit-card">
              <i className="fa fa-laptop" aria-hidden="true"></i>
              <h3>Easy Online Booking</h3>
              <p>Complete pickup, drop, vehicle and passenger details in the same booking form used on the homepage.</p>
            </article>
            <article className="rl-benefit-card">
              <i className="fa fa-exchange" aria-hidden="true"></i>
              <h3>One-Way Taxi Service</h3>
              <p>Book a one-way outstation trip without arranging a return cab, or switch to round trip if needed.</p>
            </article>
            <article className="rl-benefit-card">
              <i className="fa fa-calculator" aria-hidden="true"></i>
              <h3>Transparent Fare Calculation</h3>
              <p>Estimates use billed distance, the published vehicle rate and driver bata, with extras listed at review.</p>
            </article>
            <article className="rl-benefit-card">
              <i className="fa fa-car" aria-hidden="true"></i>
              <h3>Multiple Vehicle Options</h3>
              <p>Choose Sedan, SUV or Crysta based on passengers and comfort, at the existing tariff rates.</p>
            </article>
            <article className="rl-benefit-card">
              <i className="fa fa-map-marker" aria-hidden="true"></i>
              <h3>Door-to-Door Pickup &amp; Drop</h3>
              <p>Type specific addresses in the pickup and drop fields instead of being limited to a single stand.</p>
            </article>
            <article className="rl-benefit-card">
              <i className="fa fa-phone" aria-hidden="true"></i>
              <h3>Customer Support</h3>
              <p>Call <a href="tel:+919629954544">+91 96299 54544</a> for booking help. Support is listed as 24/7 on the main site.</p>
            </article>
            <article className="rl-benefit-card">
              <i className="fa fa-user" aria-hidden="true"></i>
              <h3>Experienced Drivers</h3>
              <p>Trips are fulfilled through Easy Drop Taxi’s existing driver network used for outstation travel.</p>
            </article>
            <article className="rl-benefit-card">
              <i className="fa fa-snowflake-o" aria-hidden="true"></i>
              <h3>Fully A/C Taxis</h3>
              <p>The fleet listed on the website is described as air-conditioned for intercity journeys.</p>
            </article>
          </div>
        </div>
      </section>

      <section className="rl-section">
        <div className="container">
          <h2>Popular Pickup Locations in Chennai</h2>
          <p className="rl-section-intro">
            Chennai and Chennai Airport appear in Easy Drop Taxi’s service city list. You can also type a more specific pickup in the booking form. The areas below are common points travellers often enter; they are not a guaranteed coverage list.
          </p>
          <div className="rl-place-grid">
            {['Chennai Airport', 'Central Chennai', 'Tambaram', 'Porur', 'Anna Nagar', 'Velachery', 'OMR'].map((place) => (
              <article key={place} className="rl-place-card">
                <h3>{place}</h3>
                <p>Enter this as your pickup if it matches your starting point, then continue with the fare estimate.</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="rl-section rl-section-muted">
        <div className="container">
          <h2>Popular Drop Locations in Madurai</h2>
          <p className="rl-section-intro">
            Madurai is included in the existing city list. Drop-off is editable, so you can name an airport, station, bus stand, hotel or neighbourhood. The examples below are typical destinations, not a separate service catalogue.
          </p>
          <div className="rl-place-grid">
            {['Madurai Airport', 'Madurai Junction', 'Mattuthavani', 'Madurai city areas', 'Major hotels'].map((place) => (
              <article key={place} className="rl-place-card">
                <h3>{place}</h3>
                <p>Use the drop field to set this destination before you request a fare estimate.</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="rl-section">
        <div className="container">
          <h2>How to Book</h2>
          <div className="rl-step-grid">
            <article className="rl-step-card">
              <span className="rl-step-number">1</span>
              <h3>Enter Your Trip Details</h3>
              <p>Confirm pickup, drop and your 10-digit mobile number in the booking card, then tap Get Fare Estimate.</p>
            </article>
            <article className="rl-step-card">
              <span className="rl-step-number">2</span>
              <h3>Select Your Vehicle</h3>
              <p>Choose Sedan, SUV or Crysta, then add pickup date, time and passenger name.</p>
            </article>
            <article className="rl-step-card">
              <span className="rl-step-number">3</span>
              <h3>Confirm Your Booking</h3>
              <p>Review the trip and fare summary, then confirm. The request is sent through the existing Easy Drop Taxi booking system.</p>
            </article>
          </div>
        </div>
      </section>

      <section className="rl-section rl-section-muted">
        <div className="container">
          <h2>Fare &amp; Pricing</h2>
          <p className="rl-section-intro">
            Fares are not a fixed package price. They come from the same calculation used on every Easy Drop Taxi booking.
          </p>
          <div className="rl-fare-grid">
            {VEHICLES.map((vehicle) => (
              <article key={vehicle.type} className="rl-fare-card">
                <h3>{getVehicleDisplayName(vehicle)}</h3>
                <p>₹{vehicle.rate} per km</p>
                <p>Driver bata: ₹{DRIVER_BATA}</p>
                {routeMetrics?.distanceKm ? (
                  <p>
                    Sample one-way estimate: ₹
                    {getVehiclePrice(routeMetrics.distanceKm, vehicle.rate, 'One Way').toLocaleString('en-IN')}
                  </p>
                ) : (
                  <p>Open the booking card to generate a live estimate for your route.</p>
                )}
              </article>
            ))}
          </div>
          <p className="rl-fare-note">
            One-way billing uses a {MIN_ONEWAY_KM} km minimum. Toll gate, state permit, hill station, parking and waiting time are extra, matching the note shown when you review a booking.
          </p>
          <button type="button" className="rl-select-btn" style={{ maxWidth: 280 }} onClick={scrollToBooking}>
            Get Fare Estimate
          </button>
        </div>
      </section>

      <section className="rl-section">
        <div className="container">
          <h2>Travel from Chennai to Madurai</h2>
          <div className="rl-prose">
            <p>
              This intercity taxi journey is booked as a point-to-point trip. You set the pickup in Chennai and the drop in Madurai, then travel without changing vehicles at a bus stand or railway station.
            </p>
            <p>
              The service suits individuals, couples and families, depending on the vehicle you select. Because one-way booking is supported, you do not have to arrange a return taxi unless you want a round trip.
            </p>
            <p>
              Add your preferred date and time in the booking form when you are ready. Distance and travel time shown on this page come from the same mapping tools used to estimate the fare, and they can vary with your exact addresses and traffic.
            </p>
          </div>
        </div>
      </section>

      <section className="rl-section rl-section-muted">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="rl-faq">
            {faqs.map((item) => (
              <details key={item.question}>
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="rl-section">
        <div className="container">
          <h2>Related Pages</h2>
          <p className="rl-section-intro">
            Need the opposite direction, or a general Easy Drop Taxi page? The reverse Madurai to Chennai landing page is live, along with these existing pages.
          </p>
          <div className="rl-links">
            <Link to="/madurai-to-chennai-taxi">Madurai to Chennai Taxi</Link>
            <Link to="/">Home</Link>
            <Link to="/tariff">Tariff</Link>
            <Link to="/services">Services</Link>
            <Link to="/about">About</Link>
            <Link to="/contact">Contact</Link>
          </div>
        </div>
      </section>

      <section className="rl-cta">
        <div className="container">
          <h2>Ready to Travel from Chennai to Madurai?</h2>
          <p>Book your Chennai to Madurai taxi with EasyDropTaxi.</p>
          <button type="button" className="rl-cta-btn" onClick={scrollToBooking}>
            Book Your Taxi
          </button>
        </div>
      </section>
    </main>
  );
};

export default ChennaiToMadurai;
